import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCanvassByAny,
  selectCurrentCanvass,
  selectCanvassLoading,
} from "@/store/redux/slices/canvass/canvass_slice";
import { useLoader } from "@/hooks/LoaderProvider";
import Back from "@/components/specific/Back";
import { Checkbox } from "@/config/antd";
import WebIcon from "@/components/common/Web_Icons";
import Button from "@/components/common/Button";
import Breadcrumb from "@/components/specific/Breadcrumb";
import { formatAPIDateToFullDate } from "@/utils/date_time_year_utils";

const SingleCanvass = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();

  // Get canvass data from Redux store
  const apiCanvassData = useSelector(selectCurrentCanvass);
  const isLoading = useSelector(selectCanvassLoading);

  // Fetch canvass data when component loads
  useEffect(() => {
    if (isLoading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoading, showLoader, hideLoader]);

  useEffect(() => {
    if (id) {
      const fetchCanvass = async () => {
        try {
          const res = await dispatch(
            fetchCanvassByAny({ key: "_id", value: id })
          ).unwrap();
          console.log("response data for single canvass", res);
        } catch (error) {
          console.error("Error fetching canvass data:", error);
        }
      };
      fetchCanvass();
    }
  }, [dispatch, id]);

  const [canvassData, setCanvassData] = useState({
    _id: id,
    name: "Loading...",
    address: "Loading...",
    generalInfo: {
      gender: "N/A",
      dateOfBirth: "N/A",
    },
    contact: {
      phoneNumber: "Loading...",
    },
    politicalSupport: {
      level: "Strongly Support",
    },
    voteLikelihood: {
      level: "Very Likely",
    },
    votingHistory: {
      status: "Regular Voter",
    },
    keyIssues: {
      selectedIssues: [],
    },
    interaction: {
      type: "Face-to-Face",
    },
    followUp: {
      willing: false,
    },
    campaignMaterials: {
      requested: false,
    },
    volunteering: {
      interested: false,
      type: "Face-to-Face",
    },
    donating: {
      interested: false,
      type: "Time/Volunteering",
    },
  });

  useEffect(() => {
    if (apiCanvassData) {
      setCanvassData({
        _id: apiCanvassData?._id,
        name: apiCanvassData?.fullName || "N/A",
        address: apiCanvassData?.contactInformation?.address || "N/A",
        generalInfo: {
          gender: apiCanvassData.contactInformation?.gender || "N/A",
          dateOfBirth: formatAPIDateToFullDate(
            apiCanvassData.contactInformation?.dob
          ),
        },
        contact: {
          phoneNumber: apiCanvassData?.contactInformation?.phoneNumber || "N/A",
        },
        politicalSupport: {
          level: apiCanvassData?.partySupport || "N/A",
        },
        voteLikelihood: {
          level: apiCanvassData?.likelihoodToVote || "N/A",
        },
        votingHistory: {
          status: apiCanvassData?.previousVotingHistory
            ? "Regular Voter"
            : apiCanvassData?.firstTimeVoter
            ? "First Time"
            : "Never Voted",
        },
        keyIssues: {
          selectedIssues: apiCanvassData?.keyIssues || [],
        },
        interaction: {
          type: apiCanvassData?.typeOfInteraction || "N/A",
        },
        followUp: {
          willing: apiCanvassData?.followUpNeeded || false,
        },
        campaignMaterials: {
          requested: apiCanvassData?.requestForCampaignMaterials || false,
        },
        volunteering: {
          interested: apiCanvassData?.interestedInVolunteering || false,
          type: apiCanvassData?.preferredFollowUpMethod || "Face-to-Face",
        },
        donating: {
          interested: apiCanvassData?.interestedInDonating || false,
          type:
            apiCanvassData?.preferredModeOfContribution || "Time/Volunteering",
        },
      });
    }
  }, [apiCanvassData]);

  // For issues selection
  const availableIssues = [
    "Economy/Jobs",
    "Education",
    "Healthcare",
    "Environment",
    "Immigration",
    "National Security",
    "Housing",
    "Transportation",
  ];

  // Political support options
  const supportOptions = ["Strongly Oppose", "Undecided", "Strongly Support"];
  // Vote likelihood options
  const likelihoodOptions = ["Will Not Vote", "Not Sure", "Very Likely"];
  // Voting history options
  const historyOptions = ["First Time", "Never Voted", "Regular Voter"];

  // Define breadcrumb items
  const breadcrumbItems = [
    { title: "Canvass", link: "/dashboard/canvass" },
    { title: "Household Details", link: null },
  ];

  const handleBackClick = () => {
    navigate("/dashboard/canvass");
  };

  return (
    <div className="w-full">
      <div className="mb-3">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="mb-4">
        <Back text="Back" onClick={handleBackClick} />
      </div>

      {/* Grid layout with 70/30 split */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Left panel - Voter details (spans 2 columns = ~70%) */}
        <div className="col-span-2">
          {/* General Info */}
          <div className="rounded-lg mb-4 bg-secondary p-2">
            <div className="px-6 py-2">
              <h2 className="text-regular text-left font-medium text-darkGrey">
                GENERAL INFO
              </h2>
            </div>

            <div className="bg-white rounded-b-lg m-4">
              <div className="flex justify-between px-6 py-2 border-b border-secondary">
                <p className="text-bold font-bold text-black">Gender</p>
                <p className="text-bold font-normal text-darkGrey">
                  {canvassData.generalInfo.gender}
                </p>
              </div>

              <div className="flex justify-between px-6 py-5">
                <p className="text-bold font-bold text-black">Date of Birth</p>
                <p className="text-bold font-normal text-darkGrey">
                  {canvassData.generalInfo.dateOfBirth}
                </p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="rounded-lg mb-4 bg-secondary p-2">
            <div className="px-6 py-2">
              <h2 className="text-regular text-left font-medium text-darkGrey">
                CONTACT
              </h2>
            </div>

            <div className="bg-white rounded-b-lg m-4">
              <div className="px-6 py-5">
                <div>
                  <p className="text-bold text-left font-bold text-black mb-1">
                    Phone Number
                  </p>
                  <div className="flex justify-between items-center">
                    <p className="text-bold font-normal text-darkGrey">
                      {canvassData.contact.phoneNumber}
                    </p>
                    <div className="flex gap-2">
                      <div className=" flex items-center justify-center">
                        <WebIcon icon="IconCall" size="large" />
                      </div>
                      <div className=" flex items-center justify-center">
                        <WebIcon icon="MessageBg" size="large" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Political Party Support */}
          <div className="rounded-lg mb-4 bg-secondary p-2">
            <div className="px-6 py-2">
              <h2 className="text-regular text-left font-medium text-darkGrey">
                POLITICAL PARTY SUPPORT
              </h2>
            </div>

            <div className="bg-white rounded-b-lg m-4">
              <div className="px-6 py-5">
                <div className="text-left">
                  <h3 className="heading-medium font-semibold mb-2">
                    Political Party Support
                  </h3>
                  <p className="text-regular text-darkGrey mb-6">
                    Level of the voter&apos;s support for our party?
                  </p>
                </div>

                <div className="border-t border-secondary pt-6">
                  <div className="flex flex-wrap gap-4 w-full">
                    {supportOptions.map((option) => (
                      <Button
                        key={option}
                        text={option}
                        className="flex-1 !py-2 min-w-[100px] text-center overflow-hidden whitespace-nowrap text-ellipsis md:whitespace-nowrap md:min-w-[120px]"
                        rounded="full"
                        customBgColor={
                          canvassData.politicalSupport.level === option
                            ? "primary"
                            : "white"
                        }
                        customTextColor={
                          canvassData.politicalSupport.level === option
                            ? "white"
                            : "darkGrey"
                        }
                        icon={
                          canvassData.politicalSupport.level === option ? (
                            <WebIcon
                              icon="Verify"
                              size="small"
                              className="invert"
                            />
                          ) : null
                        }
                        iconPosition="left"
                        onClick={() => {}} // No action - view only
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Voting Likelihood */}
          <div className="rounded-lg mb-4 bg-secondary p-2">
            <div className="px-6 py-2">
              <h2 className="text-regular text-left font-medium text-darkGrey">
                VOTING LIKELIHOOD
              </h2>
            </div>

            <div className="bg-white rounded-lg m-4">
              <div className="px-6 py-5">
                <div className="text-left">
                  <h3 className="heading-medium font-semibold mb-2">
                    Likelihood to Vote
                  </h3>
                  <p className="text-regular text-darkGrey mb-6">
                    How likely is the interviewee to vote?
                  </p>
                </div>

                <div className="border-t border-secondary pt-6">
                  <div className="flex flex-wrap gap-4 w-full">
                    {likelihoodOptions.map((option) => (
                      <Button
                        key={option}
                        text={option}
                        className="flex-1 !py-2 min-w-[100px] text-center overflow-hidden whitespace-nowrap text-ellipsis md:whitespace-normal md:min-w-[120px]"
                        rounded="full"
                        customBgColor={
                          canvassData.voteLikelihood.level === option
                            ? "primary"
                            : "white"
                        }
                        customTextColor={
                          canvassData.voteLikelihood.level === option
                            ? "white"
                            : "darkGrey"
                        }
                        icon={
                          canvassData.voteLikelihood.level === option ? (
                            <WebIcon
                              icon="Verify"
                              size="small"
                              className="invert"
                            />
                          ) : null
                        }
                        iconPosition="left"
                        onClick={() => {}} // No action - view only
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Voting History */}
          <div className="rounded-lg mb-4 bg-secondary p-2">
            <div className="px-6 py-2">
              <h2 className="text-regular text-left font-medium text-darkGrey">
                VOTING HISTORY
              </h2>
            </div>

            <div className="bg-white rounded-lg m-4">
              <div className="px-6 py-5">
                <div className="text-left">
                  <h3 className="heading-medium font-semibold mb-2">
                    Voting History
                  </h3>
                  <p className="text-regular text-darkGrey mb-6">
                    What is the voting history of the interviewee?
                  </p>
                </div>

                <div className="border-t border-secondary pt-6">
                  <div className="flex flex-wrap gap-4 w-full">
                    {historyOptions.map((option) => (
                      <Button
                        key={option}
                        text={option}
                        className="flex-1 !py-2 min-w-[100px] text-center overflow-hidden whitespace-nowrap text-ellipsis md:whitespace-normal md:min-w-[120px]"
                        rounded="full"
                        customBgColor={
                          canvassData.votingHistory.status === option
                            ? "primary"
                            : "white"
                        }
                        customTextColor={
                          canvassData.votingHistory.status === option
                            ? "white"
                            : "darkGrey"
                        }
                        icon={
                          canvassData.votingHistory.status === option ? (
                            <WebIcon
                              icon="Verify"
                              size="small"
                              className="invert"
                            />
                          ) : null
                        }
                        iconPosition="left"
                        onClick={() => {}} // No action - view only
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Issues */}
          <div className="rounded-lg mb-4 bg-secondary p-2">
            <div className="px-6 py-2">
              <h2 className="text-regular text-left font-medium text-darkGrey">
                KEY ISSUES
              </h2>
            </div>

            <div className="bg-white rounded-lg m-4">
              <div className="px-6 py-5">
                <div className="text-left">
                  <h3 className="heading-medium font-semibold mb-2">
                    Key Issues
                  </h3>
                  <p className="text-regular text-darkGrey mb-6">
                    What issues matter most to the interviewee?
                  </p>
                </div>

                <div className="border-t border-secondary pt-6">
                  <div className="grid grid-cols-2 gap-4">
                    {availableIssues.map((issue) => (
                      <div key={issue} className="flex items-center">
                        <Checkbox
                          checked={canvassData.keyIssues.selectedIssues.includes(
                            issue
                          )}
                          disabled={true} // Disabled for view-only mode
                        />
                        <span className="ml-2">{issue}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Type of Interaction */}
          <div className="rounded-lg mb-4 bg-secondary p-2">
            <div className="px-6 py-2">
              <h2 className="text-regular text-left font-medium text-darkGrey">
                TYPE OF INTERACTION
              </h2>
            </div>

            <div className="bg-white rounded-lg m-4">
              <div className="px-6 py-5">
                <div className="text-left">
                  <h3 className="heading-medium font-semibold mb-2">
                    Type of Interaction
                  </h3>
                  <p className="text-regular text-darkGrey mb-6">
                    How did you interact with the interviewee?
                  </p>
                </div>

                <div className="border-t border-secondary pt-6">
                  <div className="flex flex-wrap gap-4 w-full">
                    {["Door-to-Door", "Phone Call", "Face-to-Face"].map(
                      (option) => (
                        <Button
                          key={option}
                          text={option}
                          className="flex-1 !py-2 min-w-[100px] text-center overflow-hidden whitespace-nowrap text-ellipsis md:whitespace-normal md:min-w-[120px]"
                          rounded="full"
                          customBgColor={
                            canvassData.interaction.type === option
                              ? "primary"
                              : "white"
                          }
                          customTextColor={
                            canvassData.interaction.type === option
                              ? "white"
                              : "darkGrey"
                          }
                          icon={
                            canvassData.interaction.type === option ? (
                              <WebIcon
                                icon="Verify"
                                size="small"
                                className="invert"
                              />
                            ) : null
                          }
                          iconPosition="left"
                          onClick={() => {}} // No action - view only
                        />
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Follow-up */}
          <div className="rounded-lg mb-4 bg-secondary p-2">
            <div className="px-6 py-2">
              <h2 className="text-regular text-left font-medium text-darkGrey">
                FOLLOW-UP
              </h2>
            </div>

            <div className="bg-white rounded-lg m-4">
              <div className="px-6 py-5">
                <div className="text-left">
                  <h3 className="heading-medium font-semibold mb-2">
                    Follow-up Needed?
                  </h3>
                  <p className="text-regular text-darkGrey mb-6">
                    Does this interviewee require a follow-up?
                  </p>
                </div>

                <div className="border-t border-secondary pt-6">
                  {/* Yes/No Toggle Switch */}
                  <div className="relative h-10 w-full rounded-full bg-secondary overflow-hidden">
                    {/* Background container */}
                    <div className="absolute inset-0 flex">
                      {/* Static text elements - always visible */}
                      <div className="w-1/2 h-full flex items-center px-4 z-10">
                        {!canvassData.followUp.willing && (
                          <WebIcon
                            icon="Verify"
                            size="small"
                            className="invert mr-1"
                          />
                        )}
                        <span
                          className={`transition-colors duration-300 ${
                            !canvassData.followUp.willing
                              ? "text-white"
                              : "text-darkGrey"
                          }`}>
                          No
                        </span>
                      </div>
                      <div className="w-1/2 h-full flex items-center px-4 z-10">
                        {canvassData.followUp.willing && (
                          <WebIcon
                            icon="Verify"
                            size="small"
                            className="invert mr-1"
                          />
                        )}
                        <span
                          className={`transition-colors duration-300 ${
                            canvassData.followUp.willing
                              ? "text-white"
                              : "text-darkGrey"
                          }`}>
                          Yes
                        </span>
                      </div>
                    </div>

                    {/* Sliding highlight that moves */}
                    <div
                      className="absolute top-0 bottom-0 w-1/2 bg-primary rounded-full transition-transform duration-300 ease-in-out"
                      style={{
                        transform: canvassData.followUp.willing
                          ? "translateX(100%)"
                          : "translateX(0)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Campaign Materials */}
          <div className="rounded-lg mb-4 bg-secondary p-2">
            <div className="px-6 py-2">
              <h2 className="text-regular text-left font-medium text-darkGrey">
                CAMPAIGN MATERIALS
              </h2>
            </div>

            <div className="bg-white rounded-lg m-4">
              <div className="px-6 py-5">
                <div className="text-left">
                  <h3 className="heading-medium font-semibold mb-2">
                    Request for Campaign Materials?
                  </h3>
                  <p className="text-regular text-darkGrey mb-6">
                    Is the interviewee requesting campaign materials?
                  </p>
                </div>

                <div className="border-t border-secondary pt-6">
                  {/* Yes/No Toggle Switch */}
                  <div className="relative h-10 w-full rounded-full bg-secondary overflow-hidden">
                    {/* Background container */}
                    <div className="absolute inset-0 flex">
                      {/* Static text elements - always visible */}
                      <div className="w-1/2 h-full flex items-center px-4 z-10">
                        {!canvassData.campaignMaterials.requested && (
                          <WebIcon
                            icon="Verify"
                            size="small"
                            className="invert mr-1"
                          />
                        )}
                        <span
                          className={`transition-colors duration-300 ${
                            !canvassData.campaignMaterials.requested
                              ? "text-white"
                              : "text-darkGrey"
                          }`}>
                          No
                        </span>
                      </div>
                      <div className="w-1/2 h-full flex items-center px-4 z-10">
                        {canvassData.campaignMaterials.requested && (
                          <WebIcon
                            icon="Verify"
                            size="small"
                            className="invert mr-1"
                          />
                        )}
                        <span
                          className={`transition-colors duration-300 ${
                            canvassData.campaignMaterials.requested
                              ? "text-white"
                              : "text-darkGrey"
                          }`}>
                          Yes
                        </span>
                      </div>
                    </div>

                    {/* Sliding highlight that moves */}
                    <div
                      className="absolute top-0 bottom-0 w-1/2 bg-primary rounded-full transition-transform duration-300 ease-in-out"
                      style={{
                        transform: canvassData.campaignMaterials.requested
                          ? "translateX(100%)"
                          : "translateX(0)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Volunteering - Separate section */}
          <div className="rounded-lg mb-4 bg-secondary p-2">
            <div className="px-6 py-2">
              <h2 className="text-regular text-left font-medium text-darkGrey">
                VOLUNTEERING
              </h2>
            </div>

            <div className="bg-white rounded-lg m-4">
              <div className="px-6 py-5">
                <div className="text-left">
                  <h3 className="heading-medium font-semibold mb-2">
                    Interested in Volunteering?
                  </h3>
                  <p className="text-regular text-darkGrey mb-6">
                    What type of volunteering is this interviewee interested in?
                  </p>
                </div>

                <div className="border-t border-secondary pt-6">
                  <div className="flex flex-wrap gap-4 w-full">
                    {["Door-to-Door", "Phone Call", "Face-to-Face"].map(
                      (option) => (
                        <Button
                          key={option}
                          text={option}
                          className="flex-1 !py-2 min-w-[100px] text-center overflow-hidden whitespace-nowrap text-ellipsis md:whitespace-normal md:min-w-[120px]"
                          rounded="full"
                          customBgColor={
                            canvassData.volunteering.type === option
                              ? "primary"
                              : "white"
                          }
                          customTextColor={
                            canvassData.volunteering.type === option
                              ? "white"
                              : "darkGrey"
                          }
                          icon={
                            canvassData.volunteering.type === option ? (
                              <WebIcon
                                icon="Verify"
                                size="small"
                                className="invert"
                              />
                            ) : null
                          }
                          iconPosition="left"
                          onClick={() => {}} // No action - view only
                        />
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Donating - Separate section */}
          <div className="rounded-lg mb-4 bg-secondary p-2">
            <div className="px-6 py-2">
              <h2 className="text-regular text-left font-medium text-darkGrey">
                DONATING
              </h2>
            </div>

            <div className="bg-white rounded-lg m-4">
              <div className="px-6 py-5">
                <div className="text-left">
                  <h3 className="heading-medium font-semibold mb-2">
                    Interested in Donating?
                  </h3>
                  <p className="text-regular text-darkGrey mb-6">
                    Is this interviewee interested in donating to the campaign?
                  </p>
                </div>

                <div className="border-t border-secondary pt-6">
                  {/* Yes/No Toggle Switch */}
                  <div className="relative h-10 w-full rounded-full bg-secondary overflow-hidden">
                    {/* Background container */}
                    <div className="absolute inset-0 flex">
                      {/* Static text elements - always visible */}
                      <div className="w-1/2 h-full flex items-center px-4 z-10">
                        {!canvassData.donating.interested && (
                          <WebIcon
                            icon="Verify"
                            size="small"
                            className="invert mr-1"
                          />
                        )}
                        <span
                          className={`transition-colors duration-300 ${
                            !canvassData.donating.interested
                              ? "text-white"
                              : "text-darkGrey"
                          }`}>
                          No
                        </span>
                      </div>
                      <div className="w-1/2 h-full flex items-center px-4 z-10">
                        {canvassData.donating.interested && (
                          <WebIcon
                            icon="Verify"
                            size="small"
                            className="invert mr-1"
                          />
                        )}
                        <span
                          className={`transition-colors duration-300 ${
                            canvassData.donating.interested
                              ? "text-white"
                              : "text-darkGrey"
                          }`}>
                          Yes
                        </span>
                      </div>
                    </div>

                    {/* Sliding highlight that moves */}
                    <div
                      className="absolute top-0 bottom-0 w-1/2 bg-primary rounded-full transition-transform duration-300 ease-in-out"
                      style={{
                        transform: canvassData.donating.interested
                          ? "translateX(100%)"
                          : "translateX(0)",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right panel - Action buttons and metrics (spans 1 column = ~30%) */}
        <div className="col-span-1">
          {/* Action Buttons Card */}
          <div className="bg-white p-5 rounded-lg mb-6">
            {/* Main heading with voter name */}
            <h1 className="heading-large text-center font-bold mb-2">
              {canvassData.name}
            </h1>
            <p className="text-small text-center text-lighterGrey mb-6">
              {canvassData.address}
            </p>

            <div className="grid grid-cols-4 gap-4 mb-6">
              <div className="flex flex-col items-center">
                <div className=" flex items-center justify-center mb-2">
                  <WebIcon icon="IconCall" size="large" className="w-10 h-10" />
                </div>
                <span className="text-small whitespace-nowrap">Phone Call</span>
              </div>

              <div className="flex flex-col items-center">
                <div className=" flex items-center justify-center mb-2">
                  <WebIcon
                    icon="MessageBg"
                    size="large"
                    className="w-10 h-10"
                  />
                </div>
                <span className="text-small">Message</span>
              </div>

              <div className="flex flex-col items-center">
                <div className=" flex items-center justify-center mb-2">
                  <WebIcon
                    icon="LocationBg"
                    size="large"
                    className="w-10 h-10"
                  />
                </div>
                <span className="text-small">Location</span>
              </div>

              <div className="flex flex-col items-center">
                <div className=" flex items-center justify-center mb-2">
                  <WebIcon icon="EditBg" size="large" className="w-10 h-10" />
                </div>
                <span className="text-small">Modify</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleCanvass;
